import type { SVGProps } from "react";

export type IconName =
  | "brand"
  | "select"
  | "pen"
  | "rectangle"
  | "ellipse"
  | "triangle"
  | "eraser"
  | "sparkles"
  | "undo"
  | "redo"
  | "eye"
  | "export"
  | "image"
  | "folder"
  | "plus"
  | "minus"
  | "trash"
  | "copy"
  | "chevron-left"
  | "chevron-right"
  | "chevron-down"
  | "zoom-in"
  | "zoom-out"
  | "fit"
  | "shield"
  | "check"
  | "grid"
  | "info"
  | "download"
  | "close"
  | "refresh"
  | "shapes"
  | "polygon"
  | "more"
  | "compare"
  | "outline"
  | "keyboard"
  | "home"
  | "upload";

interface IconProps extends SVGProps<SVGSVGElement> {
  name: IconName;
  size?: number;
}

export function Icon({ name, size = 24, ...props }: IconProps) {
  if (name === "brand") {
    return (
      <svg
        viewBox="0 0 24 24"
        width={size}
        height={size}
        aria-hidden="true"
        {...props}
      >
        <path
          fill="currentColor"
          d="M2 2h5.2v5.2H2zm7.4 0h5.2v5.2H9.4zm7.4 0H22v5.2h-5.2zM2 9.4h5.2v5.2H2zm7.4 0h5.2v5.2H9.4zm7.4 0H22v5.2h-5.2zM2 16.8h5.2V22H2zm7.4 0h5.2V22H9.4z"
        />
      </svg>
    );
  }

  const common = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };

  const path = (() => {
    switch (name) {
      case "select":
        return <path d="m5 3 13 9-6.2 1.5L9 20z" />;
      case "pen":
        return (
          <>
            <path d="m4 20 4.2-1 10.6-10.6a2.1 2.1 0 0 0-3-3L5.2 16z" />
            <path d="m13.8 7.4 3 3M5.2 16l3 3" />
          </>
        );
      case "rectangle":
        return <rect x="4" y="5" width="16" height="14" rx="1.5" />;
      case "ellipse":
        return <ellipse cx="12" cy="12" rx="8.5" ry="7.5" />;
      case "triangle":
        return <path d="M12 4.5 20.5 19.5h-17Z" />;
      case "eraser":
        return (
          <>
            <path d="m4 15 8.5-9a2 2 0 0 1 2.9 0l3 3a2 2 0 0 1 0 2.8L11 19H7z" />
            <path d="m10 9 7 7M11 19h9" />
          </>
        );
      case "sparkles":
        return (
          <>
            <path d="M12 2.8c.8 3.5 2.5 5.2 6 6-3.5.8-5.2 2.5-6 6-.8-3.5-2.5-5.2-6-6 3.5-.8 5.2-2.5 6-6Z" />
            <path d="M19 15.5c.4 1.8 1.3 2.7 3 3-1.7.4-2.6 1.3-3 3-.4-1.7-1.3-2.6-3-3 1.7-.3 2.6-1.2 3-3ZM4.5 3.5c.3 1.3 1 2 2.3 2.3-1.3.3-2 1-2.3 2.2-.3-1.2-1-1.9-2.3-2.2 1.3-.3 2-1 2.3-2.3Z" />
          </>
        );
      case "undo":
        return <path d="M9 7 4 12l5 5M5 12h8a6 6 0 0 1 6 6" />;
      case "redo":
        return <path d="m15 7 5 5-5 5m4-5h-8a6 6 0 0 0-6 6" />;
      case "eye":
        return (
          <>
            <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
            <circle cx="12" cy="12" r="2.7" />
          </>
        );
      case "export":
      case "download":
        return (
          <>
            <path d="M12 4v11m0 0-4.5-4.5M12 15l4.5-4.5" />
            <path d="M4 15v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4" />
          </>
        );
      case "image":
        return (
          <>
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <circle cx="8" cy="9" r="1.5" />
            <path d="m5 18 5-5 3 3 2-2 4 4" />
          </>
        );
      case "folder":
        return <path d="M3 6h7l2 2h9v10H3z" />;
      case "plus":
        return <path d="M12 5v14M5 12h14" />;
      case "minus":
        return <path d="M5 12h14" />;
      case "trash":
        return (
          <>
            <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" />
            <path d="M10 11v5m4-5v5" />
          </>
        );
      case "copy":
        return (
          <>
            <rect x="9" y="9" width="11" height="11" rx="2" />
            <path d="M15 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h3" />
          </>
        );
      case "chevron-left":
        return <path d="m15 5-7 7 7 7" />;
      case "chevron-right":
        return <path d="m9 5 7 7-7 7" />;
      case "chevron-down":
        return <path d="m5 9 7 7 7-7" />;
      case "zoom-in":
        return (
          <>
            <circle cx="10.5" cy="10.5" r="6.5" />
            <path d="m15.5 15.5 5 5M10.5 7.5v6m-3-3h6" />
          </>
        );
      case "zoom-out":
        return (
          <>
            <circle cx="10.5" cy="10.5" r="6.5" />
            <path d="m15.5 15.5 5 5M7.5 10.5h6" />
          </>
        );
      case "fit":
        return <path d="M9 3H3v6m12-6h6v6M9 21H3v-6m12 6h6v-6" />;
      case "shield":
        return <path d="M12 3 5 6v5c0 4.6 2.8 8 7 10 4.2-2 7-5.4 7-10V6z" />;
      case "check":
        return <path d="m5 12 4 4L19 6" />;
      case "grid":
        return (
          <>
            <rect x="3" y="3" width="7" height="7" />
            <rect x="14" y="3" width="7" height="7" />
            <rect x="3" y="14" width="7" height="7" />
            <rect x="14" y="14" width="7" height="7" />
          </>
        );
      case "info":
        return (
          <>
            <circle cx="12" cy="12" r="9" />
            <path d="M12 11v6m0-10h.01" />
          </>
        );
      case "close":
        return <path d="m6 6 12 12M18 6 6 18" />;
      case "refresh":
        return (
          <>
            <path d="M20 8V3l-2 2a8 8 0 1 0 2 8" />
            <path d="M20 3h-5" />
          </>
        );
      case "shapes":
        return (
          <>
            <rect x="3.5" y="10.5" width="10" height="10" rx="1.5" />
            <circle cx="15.5" cy="8.5" r="5" />
          </>
        );
      case "polygon":
        return (
          <>
            <path d="M5 8.5 11 4l8 5-2 9.5-10 1.5z" />
            <circle cx="5" cy="8.5" r="1.6" fill="currentColor" />
            <circle cx="11" cy="4" r="1.6" fill="currentColor" />
            <circle cx="19" cy="9" r="1.6" fill="currentColor" />
            <circle cx="17" cy="18.5" r="1.6" fill="currentColor" />
            <circle cx="7" cy="20" r="1.6" fill="currentColor" />
          </>
        );
      case "more":
        return (
          <>
            <circle cx="5.5" cy="12" r="1.2" fill="currentColor" />
            <circle cx="12" cy="12" r="1.2" fill="currentColor" />
            <circle cx="18.5" cy="12" r="1.2" fill="currentColor" />
          </>
        );
      case "compare":
        return (
          <>
            <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
            <path d="M12 2.5v19" />
            <path d="M12 4.5h6.5a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H12z" fill="currentColor" stroke="none" opacity=".35" />
          </>
        );
      case "outline":
        return <rect x="4" y="4" width="16" height="16" rx="2" strokeDasharray="3.2 2.6" />;
      case "keyboard":
        return (
          <>
            <rect x="2.5" y="6" width="19" height="12" rx="2" />
            <path d="M6.5 10h.01M10 10h.01M14 10h.01M17.5 10h.01M8 14h8" />
          </>
        );
      case "home":
        return <path d="M4 11 12 4l8 7M6 9.5V20h12V9.5" />;
      case "upload":
        return (
          <>
            <path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5" />
            <path d="M4 15v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4" />
          </>
        );
      default:
        return null;
    }
  })();

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      {...common}
      {...props}
    >
      {path}
    </svg>
  );
}
